import { InvoiceStatusItem } from "@/@types/Sells/get-details";
import Badge from "@/components/shared/Badge/Badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getIntegrationWithIcon } from "@/utils/integrations";
import { ExternalLink, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import FileTextCircle from "@/assets/icons/FileTextCircle";
import {
  invoiceStatusTone,
  mapInvoiceStatusToBadge,
} from "@/utils/invoiceStatus";

interface InvoiceStatusesSectionProps {
  invoices: InvoiceStatusItem[];
}

function formatInvoiceDate(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale, {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function InvoiceField({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  if (!value) return null;

  return (
    <article className="space-y-1">
      <p className="text-muted text-sm">{label}</p>
      <span className="text-colorText text-sm">{value}</span>
    </article>
  );
}

export default function InvoiceStatusesSection({
  invoices,
}: InvoiceStatusesSectionProps) {
  const { t, i18n } = useTranslation(["sales"]);

  if (!invoices.length) return null;

  return (
    <div className="md:space-y-6 space-y-4">
      <div className="flex items-center gap-2">
        <FileTextCircle />
        <h2 className="text-colorText font-semibold">{t("invoiceStatus")}</h2>
      </div>

      <div className="space-y-4">
        {invoices.map((inv, idx) => {
          const tone = invoiceStatusTone(inv.status);
          const integrationLabel =
            getIntegrationWithIcon(inv.integration) || (
              <span className="text-colorText text-sm">
                {inv.integration_name || inv.integration || "—"}
              </span>
            );
          const hasDocuments = Boolean(
            inv.pdf_url || inv.xml_url || inv.link_prefeitura,
          );

          return (
            <div
              key={inv.integration || idx}
              className={cn(
                "rounded-lg border p-4 transition-colors",
                tone === "success" && "border-lime-500/30 bg-lime-500/5",
                tone === "error" && "border-red-500/30 bg-red-500/5",
                tone === "neutral" && "border-zinc-700 bg-zinc-800/30",
              )}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 min-w-0">
                  {integrationLabel}
                </div>
                <Badge
                  status={mapInvoiceStatusToBadge(inv.status)}
                  text={inv.status ?? "—"}
                />
              </div>

              <section className="grid gap-4 grid-cols-1 lg:grid-cols-2 text-sm mt-4">
                <InvoiceField
                  label={t("invoiceNumber")}
                  value={inv.numero}
                />
                <InvoiceField
                  label={t("invoiceVerificationCode")}
                  value={inv.codigo_verificacao}
                />
                <InvoiceField
                  label={t("invoiceStatusReason")}
                  value={inv.status_reason}
                />
                <InvoiceField
                  label={t("invoiceUpdatedAt")}
                  value={
                    inv.updated_at
                      ? formatInvoiceDate(inv.updated_at, i18n.language)
                      : null
                  }
                />
              </section>

              {hasDocuments && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {inv.pdf_url && (
                    <Button variant="secondary" size="sm" asChild>
                      <a
                        href={inv.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FileText size={14} />
                        {t("viewNotePdf")}
                        <ExternalLink size={12} />
                      </a>
                    </Button>
                  )}
                  {inv.xml_url && (
                    <Button variant="secondary" size="sm" asChild>
                      <a
                        href={inv.xml_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FileText size={14} />
                        {t("viewNoteXml")}
                        <ExternalLink size={12} />
                      </a>
                    </Button>
                  )}
                  {inv.link_prefeitura && (
                    <Button variant="secondary" size="sm" asChild>
                      <a
                        href={inv.link_prefeitura}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink size={14} />
                        {t("viewCityHall")}
                      </a>
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
